/**
 * EquipmentByTypePanel
 * /api/safetyequipment/grouped/
 * Equipment grouped by global type with availability breakdown.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layers, RefreshCw, Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const EquipmentByTypePanel = () => {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = () => {
    setLoading(true); setError(null);
    const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
    axios.get(`${API_BASE}/api/safetyequipment/grouped/`, { headers })
      .then(r => setData(Array.isArray(r.data) ? r.data : []))
      .catch(() => setError('Could not load equipment types.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">
      {/* header */}
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-brand-500 bg-steel-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
            <Layers size={13} className="text-white" />
          </div>
          <span className="text-xs font-semibold text-slate-800">Equipment by Type</span>
          {!loading && data.length > 0 && (
            <span className="text-[10px] text-slate-400">{data.length} types</span>
          )}
        </div>
        <button onClick={load} className="p-1 rounded hover:bg-steel-200 text-slate-400 hover:text-slate-600 transition-colors">
          <RefreshCw size={12} />
        </button>
      </div>

      {loading && <div className="flex-1 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-slate-300" /></div>}
      {!loading && error && <div className="flex-1 flex items-center justify-center text-xs text-rose-400 px-4 text-center">{error}</div>}

      {!loading && !error && data.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-300">
          <Layers size={28} />
          <span className="text-xs">No equipment types</span>
        </div>
      )}

      {!loading && !error && data.length > 0 && (
        <div className="flex-1 min-h-0 flex flex-col">
          {/* column headers */}
          <div className="flex items-center gap-2 px-3 py-1 border-b border-steel-100 bg-steel-50 flex-shrink-0">
            <span className="flex-1 text-[9px] font-semibold text-slate-400 uppercase tracking-wide">Type / Locations</span>
            <span className="w-20 text-[9px] font-semibold text-slate-400 uppercase tracking-wide text-center">Availability</span>
            <span className="w-10 text-[9px] font-semibold text-slate-400 uppercase tracking-wide text-right">Avail</span>
            <span className="w-10 text-[9px] font-semibold text-slate-400 uppercase tracking-wide text-right">Total</span>
          </div>

          <div className="flex-1 overflow-y-auto">
            {data.map((t, i) => {
              const pct   = t.total_count > 0 ? Math.round((t.available_count / t.total_count) * 100) : 0;
              const color = pct >= 70 ? '#10B981' : pct >= 30 ? '#F59E0B' : '#EF4444';
              return (
                <div key={t.global_type_id}
                  className={`flex items-center gap-2 px-3 py-1.5 border-b border-steel-50 hover:bg-steel-50 ${i % 2 === 0 ? '' : 'bg-slate-50/40'}`}>

                  {/* type name + locations */}
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    {t.picture_url && (
                      <img src={t.picture_url} alt="" className="w-6 h-6 object-contain flex-shrink-0 rounded" />
                    )}
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold text-slate-700 truncate">{t.type_name}</p>
                      {t.locations?.length > 0 && (
                        <p className="text-[9px] text-slate-400 truncate">
                          {t.locations.slice(0, 3).join(', ')}
                          {t.locations.length > 3 && ` +${t.locations.length - 3}`}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* availability bar */}
                  <div className="w-20 flex items-center gap-1 flex-shrink-0">
                    <div className="flex-1 h-1.5 bg-steel-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                    <span className="text-[9px] text-slate-400 w-6 text-right">{pct}%</span>
                  </div>

                  <span className="w-10 text-[11px] font-bold text-right" style={{ color }}>
                    {t.available_count}
                  </span>
                  <span className="w-10 text-[11px] text-slate-400 text-right">
                    {t.total_count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentByTypePanel;
